import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Mail, Globe, AlertTriangle } from 'lucide-react';
import { SEO } from '../components/common/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';

export const TermsPage = () => {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="terms-page">
      <SEO 
        title="Terms & Conditions - JustOffs" 
        description="Read the terms and conditions for using JustOffs platform." 
      />
      <Header />
      
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Back Link */}
        <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-orange-500 mb-6 transition">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        {/* Header */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 mb-8 text-center">
          <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Terms & Conditions</h1>
          <p className="text-slate-400">Please read before using JustOffs</p>
          <p className="text-slate-500 text-sm mt-2">Effective Date: 01/04/2026</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 space-y-8">
          {/* Intro */}
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
            <p className="text-slate-700 leading-relaxed">
              By using JustOffs, you agree to the following terms.
            </p>
          </div>

          {/* Section 1 */}
          <section>
            <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center text-sm font-bold">1</span>
              Platform Usage
            </h2>
            <p className="text-slate-600">
              JustOffs is a platform that allows businesses to post deals, offers, and listings for local visibility.
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center text-sm font-bold">2</span>
              User Responsibilities
            </h2>
            <p className="text-slate-600 mb-3">You agree:</p>
            <ul className="space-y-2 text-slate-600">
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                To provide accurate and truthful information
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                Not to post misleading, illegal, or harmful content
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                Not to misuse the platform
              </li>
            </ul>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center text-sm font-bold">3</span>
              Listings
            </h2>
            <ul className="space-y-2 text-slate-600">
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                Listings are publicly visible
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                We do not guarantee results, leads, or sales
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                We reserve the right to remove any listing without notice
              </li>
            </ul>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center text-sm font-bold">4</span>
              Payments & Featured Listings
            </h2>
            <ul className="space-y-2 text-slate-600">
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                Certain features (like featured listings) may be paid
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                Payments are non-refundable unless stated otherwise
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                Featured placement does not guarantee business results
              </li>
            </ul>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center text-sm font-bold">5</span>
              Prohibited Activities
            </h2>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-3">
              <p className="text-red-700 font-medium flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> You must not:
              </p>
            </div>
            <ul className="space-y-2 text-slate-600">
              <li className="flex items-start gap-2">
                <span className="text-red-500 mt-1">✕</span>
                Post fake offers or scams
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 mt-1">✕</span>
                Upload harmful or offensive content
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 mt-1">✕</span>
                Attempt to hack or disrupt the platform
              </li>
            </ul>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center text-sm font-bold">6</span>
              Limitation of Liability
            </h2>
            <p className="text-slate-600 mb-3">JustOffs is not responsible for:</p>
            <ul className="space-y-2 text-slate-600">
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                Business outcomes or customer interactions
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                Accuracy of listings posted by users
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                Any losses or damages arising from platform use
              </li>
            </ul>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center text-sm font-bold">7</span>
              Termination
            </h2>
            <p className="text-slate-600">
              We may suspend or remove accounts that violate these terms.
            </p>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center text-sm font-bold">8</span>
              Modifications
            </h2>
            <p className="text-slate-600">
              We may update these terms at any time. Continued use means you accept changes.
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center text-sm font-bold">9</span>
              Governing Law
            </h2>
            <p className="text-slate-600">
              These terms are governed by the laws of India.
            </p>
          </section>

          {/* Section 10 - Contact */}
          <section>
            <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center text-sm font-bold">10</span>
              Contact
            </h2>
            <div className="bg-slate-50 rounded-xl p-5 space-y-3">
              <a href="mailto:admin@justoffs.com" className="flex items-center gap-3 text-slate-700 hover:text-orange-500 transition">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-orange-500" />
                </div>
                <span>admin@justoffs.com</span>
              </a>
              <a href="https://justoffs.com/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-slate-700 hover:text-orange-500 transition">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Globe className="w-5 h-5 text-orange-500" />
                </div>
                <span>https://justoffs.com/</span>
              </a>
            </div>
          </section>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-8">
          <Link to="/" className="inline-flex items-center gap-2 bg-orange-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-orange-600 transition">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};
